import { useCallback, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import Button from "../components/Button";
import CopyButton from "../components/CopyButton";
import Drawer from "../components/Drawer";
import Modal from "../components/Modal";
import SectionHeader from "../components/SectionHeader";
import Tag from "../components/Tag";
import { apiRequest, PORTAL_URL } from "../api";
import { NO_DATA, formatDate, formatDateTime, formatNumber, formatPercent, formatSignedPercent } from "../format";
import useApiQuery from "../useApiQuery";
import { ACCOUNT_STATUS, ACCOUNT_TYPES, KYC_STATUS, PAYMENT_TONES, STATUS_ACTIONS } from "./accountsData";
import styles from "./AccountDrawer.module.css";
import formStyles from "./RewardDrawer.module.css";

// Which status changes are offered from each users.status
const NEXT_STATUSES = {
  active: ["suspended", "deactivated"],
  suspended: ["active", "deactivated"],
  deactivated: ["active"],
};

const prp = (value) => (value === null || value === undefined ? NO_DATA : `${formatNumber(value)} PRP`);
const signedPrp = (value) =>
  value === null || value === undefined ? NO_DATA : `${value > 0 ? "+" : ""}${formatNumber(value)} PRP`;

function snapshotOf(metrics) {
  if (!metrics) {
    return [];
  }
  return [
    { label: "Starting balance", value: prp(metrics.deposit) },
    { label: "Current balance", value: prp(metrics.balance) },
    { label: "Current equity", value: prp(metrics.equity) },
    { label: "Total PRP profit", value: signedPrp(metrics.profitLoss) },
    { label: "Floating PnL", value: signedPrp(metrics.equity - metrics.balance) },
    { label: "Total lots traded", value: NO_DATA },
    { label: "Daily DD", value: `${formatPercent(metrics.dailyLoss.pct)} / ${metrics.dailyLoss.limitPct}%` },
    { label: "Max DD", value: `${formatPercent(metrics.maxDrawdown.pct)} / ${metrics.maxDrawdown.limitPct}%` },
    { label: "Last trade", value: formatDateTime(metrics.lastTradeDate) },
  ];
}

function Field({ label, children, wide = false }) {
  return (
    <div className={`${styles.field} ${wide ? styles.fieldWide : ""}`}>
      <p className={`${styles.fieldLabel} label-font`}>{label}</p>
      <div className={`${styles.fieldValue} d-flex align-items-center`}>{children}</div>
    </div>
  );
}

function Note({ note }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article className={styles.note}>
      <p className={`${styles.noteBody} ${expanded ? "" : styles.noteClamped}`}>{note.body}</p>
      <button type="button" className={styles.seeMore} onClick={() => setExpanded((value) => !value)}>
        {expanded ? "See less" : "See more"}
      </button>
      <p className={styles.noteMeta}>
        {note.author} · {formatDateTime(note.at)}
      </p>
    </article>
  );
}

// Modal form whose submit posts to the API; shows the API's error inline
function useSubmit(action) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (...args) => {
    setSubmitting(true);
    setError(null);
    try {
      await action(...args);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, error, setError, submit };
}

function AccountDrawerBody({ account, onClose, onChanged }) {
  const { data, setData, status: loadStatus, reload } = useApiQuery(`/api/admin/accounts/${account.id}`);
  const [showPassword, setShowPassword] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [nextStatus, setNextStatus] = useState(null);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [reason, setReason] = useState("");

  const saveNote = useSubmit(async (body) => {
    const { note } = await apiRequest(`/api/admin/accounts/${account.id}/notes`, { method: "POST", body: { body } });
    setData((current) => current && { ...current, detail: { ...current.detail, notes: [note, ...current.detail.notes] } });
  });
  const changeStatus = useSubmit(async (status) => {
    setData(await apiRequest(`/api/admin/accounts/${account.id}/status`, { method: "POST", body: { status } }));
    onChanged();
  });
  // Start a "view as trader" session: the API mints a short-lived, audited token; we open
  // the trader portal with it in a new tab so the admin's own session stays untouched.
  const impersonate = useSubmit(async (why) => {
    const { token } = await apiRequest(`/api/admin/accounts/${account.id}/impersonate`, {
      method: "POST",
      body: { reason: why },
    });
    window.open(`${PORTAL_URL}/#token=${encodeURIComponent(token)}`, "_blank", "noopener,noreferrer");
  });

  const closeNote = useCallback(() => setNoteOpen(false), []);
  const closeStatus = useCallback(() => setNextStatus(null), []);
  const closeImpersonate = useCallback(() => setImpersonateOpen(false), []);
  // While a modal is up, Escape should only close the modal
  const closeDrawer = useCallback(() => {
    if (!noteOpen && !nextStatus && !impersonateOpen) {
      onClose();
    }
  }, [noteOpen, nextStatus, impersonateOpen, onClose]);

  // The list row shows straight away; the detail fills in once loaded
  const row = data?.account ?? account;
  const detail = data?.detail;
  const metrics = detail?.metrics;
  const status = ACCOUNT_STATUS[row.status];
  const kyc = KYC_STATUS[row.kyc];
  const qualification = metrics?.qualifyingTarget;
  const progress = qualification ? Math.min(100, Math.max(0, qualification.progress * 100)) : 0;
  const pendingNote = (
    <p className={styles.noteMeta}>
      {loadStatus === "error" ? (
        <>
          Couldn't load this account.{" "}
          <button type="button" className={styles.seeMore} onClick={reload}>
            Try again
          </button>
        </>
      ) : (
        "Loading…"
      )}
    </p>
  );

  const handleNoteSubmit = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (body && !saveNote.submitting && (await saveNote.submit(body))) {
      setDraft("");
      setNoteOpen(false);
    }
  };

  const handleStatusSubmit = async (event) => {
    event.preventDefault();
    if (!changeStatus.submitting && (await changeStatus.submit(nextStatus))) {
      setNextStatus(null);
    }
  };

  const handleImpersonateSubmit = async (event) => {
    event.preventDefault();
    const why = reason.trim();
    if (why && !impersonate.submitting && (await impersonate.submit(why))) {
      setReason("");
      setImpersonateOpen(false);
    }
  };

  return (
    <>
      <Drawer open title={row.trader} onClose={closeDrawer}>
        <div className={styles.sections}>
          <section>
            <SectionHeader title="Account overview" />
            <div className={styles.fieldGrid}>
              <Field label="Email" wide>
                {row.email}
                <CopyButton value={row.email} label="Copy email" />
              </Field>
              <Field label="MT5 login">
                {row.login ?? NO_DATA}
                {row.login ? <CopyButton value={row.login} label="Copy MT5 login" /> : null}
              </Field>
              <Field label="Master password">
                {detail?.masterPassword ? (
                  <>
                    <span className={showPassword ? "" : styles.masked}>
                      {showPassword ? detail.masterPassword : "•••••••••"}
                    </span>
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={() => setShowPassword((value) => !value)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                    </button>
                    <CopyButton value={detail.masterPassword} label="Copy master password" />
                  </>
                ) : (
                  NO_DATA
                )}
              </Field>
              <Field label="Account type">{ACCOUNT_TYPES[row.type]}</Field>
              <Field label="Status">
                <span title={status.description}>
                  <Tag tone={status.tone}>{status.label}</Tag>
                </span>
              </Field>
              <Field label="Created">{formatDate(detail?.createdAt)}</Field>
              <Field label="Activated">{formatDate(detail?.activatedAt)}</Field>
              <Field label="Referral source">{detail?.referralSource ?? NO_DATA}</Field>
              <Field label="KYC status">
                <Tag tone={kyc.tone}>{kyc.label}</Tag>
              </Field>
            </div>
          </section>

          <section>
            <SectionHeader title="Trading snapshot" />
            {!detail ? (
              pendingNote
            ) : metrics ? (
              <dl className={styles.snapshot}>
                {snapshotOf(metrics).map((item) => (
                  <div key={item.label}>
                    <dt className="label-font">{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className={styles.noteMeta}>No live metrics for this account yet.</p>
            )}
          </section>

          {metrics ? (
            <section>
              <SectionHeader title="Account progress" />
              <div className={`${styles.progressHeader} d-flex justify-content-between flex-wrap`}>
                <span className="label-font">Qualification progress</span>
                <span>
                  {formatSignedPercent(qualification.achievedPct)} / +{qualification.targetPct}% · Attempt{" "}
                  {metrics.attempt} of {metrics.freeAttempts}
                </span>
              </div>
              <div
                className={styles.progressTrack}
                role="progressbar"
                aria-label="Qualification progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress)}
              >
                <div className={styles.progressFill} style={{ width: `${progress}%` }} />
              </div>
            </section>
          ) : null}

          <section>
            <SectionHeader title="Payments & payouts" />
            {!detail ? (
              pendingNote
            ) : detail.payments.length ? (
              <ul className={styles.list}>
                {detail.payments.map((payment, index) => (
                  <li key={index} className="d-flex align-items-center justify-content-between">
                    {payment.label}
                    <Tag tone={PAYMENT_TONES[payment.status] ?? "neutral"}>{payment.status}</Tag>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.noteMeta}>No payments yet.</p>
            )}
          </section>

          <section>
            <SectionHeader title="Account timeline" />
            {!detail ? (
              pendingNote
            ) : (
              <ol className={styles.timeline}>
                {detail.timeline.map((event, index) => (
                  <li key={index}>
                    <p className={styles.timelineLabel}>{event.label}</p>
                    <p className={styles.timelineAt}>{formatDateTime(event.at)}</p>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section>
            <SectionHeader title="Internal notes" />
            {!detail ? (
              pendingNote
            ) : detail.notes.length ? (
              <div className={styles.notes}>
                {detail.notes.map((note) => (
                  <Note key={note.id} note={note} />
                ))}
              </div>
            ) : (
              <p className={styles.noteMeta}>No notes yet.</p>
            )}
          </section>

          <section>
            <SectionHeader title="Admin actions" />
            <div className={`${styles.actions} d-flex flex-wrap`}>
              {(detail ? NEXT_STATUSES[detail.userStatus] ?? [] : []).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={styles.actionButton}
                  onClick={() => {
                    changeStatus.setError(null);
                    setNextStatus(value);
                  }}
                >
                  {STATUS_ACTIONS[value].label}
                </button>
              ))}
              <button
                type="button"
                className={styles.actionButton}
                disabled={!detail}
                onClick={() => {
                  saveNote.setError(null);
                  setNoteOpen(true);
                }}
              >
                Add internal note
              </button>
              <button
                type="button"
                className={styles.actionButton}
                disabled={!detail}
                onClick={() => {
                  impersonate.setError(null);
                  setImpersonateOpen(true);
                }}
              >
                View as trader
              </button>
            </div>
          </section>
        </div>
      </Drawer>

      <Modal open={noteOpen} title="Internal note" subtitle={row.trader} onClose={closeNote}>
        <form className={styles.noteForm} onSubmit={handleNoteSubmit}>
          <textarea
            className={styles.textarea}
            aria-label="Note"
            autoFocus
            maxLength={2000}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          {saveNote.error ? <p className={formStyles.feedbackInvalid}>{saveNote.error}</p> : null}
          <Button type="submit" disabled={!draft.trim() || saveNote.submitting}>
            {saveNote.submitting ? "Saving…" : "Save"}
          </Button>
        </form>
      </Modal>

      <Modal open={impersonateOpen} title="View as trader" subtitle={row.trader} onClose={closeImpersonate}>
        <form className={styles.noteForm} onSubmit={handleImpersonateSubmit}>
          <p className={styles.noteMeta}>
            Opens the trader portal signed in as {row.trader} in a new tab. The session lasts 30 minutes,
            can&apos;t move money or change account details, and is recorded against your name. Add a reason
            for the audit log.
          </p>
          <textarea
            className={styles.textarea}
            aria-label="Reason"
            autoFocus
            maxLength={300}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {impersonate.error ? <p className={formStyles.feedbackInvalid}>{impersonate.error}</p> : null}
          <Button type="submit" disabled={!reason.trim() || impersonate.submitting}>
            {impersonate.submitting ? "Starting…" : "Open portal"}
          </Button>
        </form>
      </Modal>

      <Modal
        open={Boolean(nextStatus)}
        title={nextStatus ? STATUS_ACTIONS[nextStatus].label : ""}
        subtitle={row.trader}
        onClose={closeStatus}
      >
        <form className={formStyles.form} onSubmit={handleStatusSubmit}>
          <p className={styles.noteMeta}>{nextStatus ? STATUS_ACTIONS[nextStatus].confirm : null}</p>
          {changeStatus.error ? <p className={formStyles.feedbackInvalid}>{changeStatus.error}</p> : null}
          <div className={`${formStyles.ctas} d-flex`}>
            <Button type="button" variant="ghost" className={formStyles.cancel} onClick={closeStatus}>
              Cancel
            </Button>
            <Button type="submit" disabled={changeStatus.submitting}>
              {changeStatus.submitting ? "Saving…" : "Confirm"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// Keyed by account so opening another row starts with fresh state and a fresh fetch
export default function AccountDrawer({ account, onClose, onChanged }) {
  return account ? (
    <AccountDrawerBody key={account.id} account={account} onClose={onClose} onChanged={onChanged} />
  ) : null;
}
